<template>
  <div class="page-main">
    <div class="page-top">
      <div class="flex flex-y-center">
        <span class="title">主业务流程</span>
        <span class="c-gray m-l-10">1 → 10 顺着走：每一步写清「谁来做、在哪个页面做、做完流向哪里」</span>
      </div>
      <div class="flex flex-y-center">
        <el-button size="mini" :type="activeId === 'overview' ? 'primary' : ''" @click="activeId = 'overview'">
          流程全景
        </el-button>
      </div>
    </div>

    <div class="flow-wrap">
      <!-- 左侧：流程导航 -->
      <div class="flow-nav">
        <div
          class="nav-item"
          :class="{ active: activeId === 'overview' }"
          @click="activeId = 'overview'"
        >
          <span class="nav-no">总</span>
          <span class="nav-text">流程全景图</span>
        </div>
        <div v-for="group in groups" :key="group.name" class="nav-group">
          <div class="nav-group-name">{{ group.name }}</div>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="nav-item"
            :class="{ active: activeId === item.id }"
            @click="activeId = item.id"
          >
            <span class="nav-no">{{ item.no }}</span>
            <span class="nav-text">{{ item.title }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧：流程全景 -->
      <div class="flow-body">
        <template v-if="activeId === 'overview'">
          <div class="card">
            <div class="card-title">三条线一起看</div>
            <div class="lane" v-for="lane in lanes" :key="lane.name">
              <div class="lane-name" :style="{ color: lane.color }">{{ lane.name }}</div>
              <div class="lane-nodes">
                <template v-for="(node, i) in lane.nodes">
                  <span v-if="i > 0" :key="'a' + i" class="arrow">→</span>
                  <span
                    :key="node.title + i"
                    class="node"
                    :style="{ borderLeftColor: lane.color }"
                    :title="'点击查看：' + node.title"
                    @click="activeId = node.stepId"
                  >
                    {{ node.title }}
                  </span>
                </template>
              </div>
            </div>
            <div class="lane-tip">
              点击任意单据方块，右侧会打开那一步的操作说明；选中后右上角有【进入该页面】可直接跳转过去。
            </div>
          </div>

          <div class="card m-t-10">
            <div class="card-title">三句话记住主线</div>
            <div class="key-line">
              <span class="key-no">1</span>
              <span>卖什么、多少钱 → 全部记在<b>核算单</b>；核算单审核后自动进<b>工程协同</b>。</span>
            </div>
            <div class="key-line">
              <span class="key-no">2</span>
              <span>怎么做 → <b>工程协同</b>补制造清单并【完结】，单据自动复制到 <b>PMC</b>，PMC 再派工单、提采购申请。</span>
            </div>
            <div class="key-line">
              <span class="key-no">3</span>
              <span>料怎么进、货怎么出、钱怎么收 → <b>采购→入库</b>、<b>送货→出库</b>、<b>收款单→应收</b>。</span>
            </div>
          </div>

          <div class="card m-t-10 warn-card">
            <div class="card-title warn-title">最容易出错的三处（务必记住）</div>
            <div class="warn-line">① 工程协同没点【完结】→ PMC 里看不到这张单，采购和生产都不会启动。</div>
            <div class="warn-line">② 送货单审核后不会自动扣库存 → 必须再做一张【出库单】并审核，否则账实不符。</div>
            <div class="warn-line">③ 工单没点【完工收货入库】→ 成品数量不会进库存，发货时会出现“账上没货”。</div>
          </div>
        </template>

        <!-- 右侧：单步说明 -->
        <template v-else-if="current">
          <div class="detail-head">
            <div class="flex flex-y-center">
              <span class="step-no">{{ current.no }}</span>
              <div>
                <div class="flex flex-y-center">
                  <span class="step-title">{{ current.title }}</span>
                  <span class="role-tag">{{ current.role }}</span>
                </div>
                <div class="step-summary">{{ current.summary }}</div>
              </div>
            </div>
            <el-button v-if="current.path" size="mini" type="primary" @click="openPage(current.path)">
              进入【{{ current.pathName }}】
            </el-button>
          </div>

          <!-- 常见问题 -->
          <div v-if="current.faqs" class="card m-t-10">
            <div class="card-title">常见问题速查</div>
            <div v-for="(qa, i) in current.faqs" :key="i" class="qa">
              <div class="qa-q">问：{{ qa.q }}</div>
              <div class="qa-a">答：{{ qa.a }}</div>
            </div>
          </div>

          <div v-else class="detail-grid">
            <div class="card">
              <div class="card-title">操作步骤</div>
              <ol class="ops">
                <li v-for="(s, i) in current.steps" :key="i">{{ s }}</li>
              </ol>
            </div>

            <div class="card">
              <div class="card-title">单据状态怎么走</div>
              <div class="status-flow">
                <span v-for="(st, i) in current.status" :key="i" class="status-item">
                  {{ st }}<span v-if="i < current.status.length - 1" class="status-arrow">›</span>
                </span>
              </div>

              <div class="card-title m-t-15">上游 / 下游</div>
              <div class="io">
                <span class="io-label">上游</span>
                <span class="io-value">{{ current.up }}</span>
              </div>
              <div class="io">
                <span class="io-label">下游</span>
                <span class="io-value">{{ current.down }}</span>
              </div>

              <template v-if="current.warn && current.warn.length">
                <div class="card-title m-t-15">注意</div>
                <div v-for="(w, i) in current.warn" :key="i" class="warn-line">{{ w }}</div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// 主流程说明数据：按系统现有菜单与按钮名称整理
const STEPS = [
  {
    id: 'verify',
    no: '01',
    group: '接单与计划',
    title: '核算单（接单报价）',
    role: '业务员',
    path: '/new/verify/list',
    pathName: '核算单',
    summary: '客户下单后开的第一张单。商品、原料、工艺、费用四类明细一次录全，后面所有环节都从这张单往下走。',
    steps: [
      '进入【核算单】，点【新增核算单】；老客户翻单可以点行内【引用新增】，复制上一单再改。',
      '选客户、填交货日期，录入明细：商品（卖给客户什么）、原料（要买什么料）、工艺（要做什么工序）、费用。',
      '核对数量、单价后点【提交审核】，状态变“待审核”。',
      '负责人点【审核】，状态变“进行中”；审核通过的同时，单据自动复制到【工程协同】开始排产。',
      '审核后可打印：报价单、核算单、生产订单、制造流程；需要签合同点【生成合同】。'
    ],
    status: ['新建=草稿', '提交=待审核', '审核通过=进行中', '被退回=已驳回', '结束=已完结／已作废'],
    up: '客户档案（客户列表）',
    down: '工程协同（审核后自动生成）；销售订单、送货单、出库单都从这里取商品明细',
    warn: ['被驳回的单据改完要重新【提交审核】，才会再次流转到负责人。', '审核后不要再改价格；确需修改先【反审核】退回。']
  },
  {
    id: 'order',
    no: '02',
    group: '接单与计划',
    title: '销售订单（交付与回款）',
    role: '业务员 / 销售负责人',
    path: '/new/order/list',
    pathName: '订单',
    summary: '商务口的订单，管合同执行、开票、送货、回款。和核算单分开：核算单管“做什么、多少钱”，订单管“交没交、收没收”。',
    steps: [
      '进入【订单】，点【新增订单】，选一张核算单引用进来（商品明细自动带过来）。',
      '核对客户、交期、金额，点【提交】。',
      '负责人点【通过】；不同意就【驳回】，业务员改完重新提交（也可【撤回提交】）。',
      '通过后按需要点【开具送货单】、【开具收款单】、【开具报销单】。',
      '客户货收齐、款收齐后点【完结订单】；整单不做了点【订单作废】。'
    ],
    status: ['提交=待审批', '通过=执行中', '结束=已完结', '异常=订单作废'],
    up: '核算单（引用新增）',
    down: '送货单、收款单（进应收）、报销单（进应付）',
    warn: ['订单和核算单是两套单，两边都要走到头，账才干净。', '订单没通过就开不了送货单和收款单，先催审核。']
  },
  {
    id: 'gongcheng',
    no: '03',
    group: '接单与计划',
    title: '工程协同（制造清单）',
    role: '工程 / 技术',
    path: '/new/gongcheng/list',
    pathName: '工程协同',
    summary: '核算单审核后自动生成，工程在这里补制造清单（工序、物料、工艺路线）。点【完结】之后，单据才会进 PMC 排产。',
    steps: [
      '进入【工程协同】，页签默认在“待受理”，能看到刚审核通过的核算单。',
      '点单据进去核对客户、数量、交期。',
      '点【编辑制造清单】，补或改工序、物料、工艺路线。',
      '点【审核】；点错了用【反审核】退回，改完再审核。',
      '制造清单确认无误后点【完结】（单据多时顶部可【批量完结20条】）。'
    ],
    status: ['待受理', '受理中', '已完结（触发 PMC 复制）'],
    up: '核算单（审核后自动复制过来）',
    down: 'PMC订单（完结瞬间自动复制）、采购申请单（原料明细）',
    warn: ['不点【完结】，PMC 里就没有这张单 —— 这是“单子不见了”最常见的原因。', '制造清单是后续派工单、提采购的依据，工序和物料别漏。']
  },
  {
    id: 'pmc',
    no: '04',
    group: '接单与计划',
    title: 'PMC订单（排产派单）',
    role: 'PMC 计划员',
    path: '/new/pmc/list',
    pathName: 'PMC订单',
    summary: 'PMC 的排产看板，按 商品 / 原料 / 工艺 / 费用 四个分类干活：原料去采购，工艺派工单。',
    steps: [
      '进入【PMC订单】，单据由工程协同完结后自动带过来。',
      '点【审核】确认可以排产（错点用【反审核】）。',
      '切到【原料】：勾选明细 → 顶部【批量操作 - 生成采购申请单】。',
      '切到【工艺】：行内【生成生产工单】派给车间，或【生成委外工单】派给加工商。',
      '生产中用【设置执行中】，全部做完【设置已完成】；行内可看“制造明细 / 制造流程”。'
    ],
    status: ['待排产', '执行中', '已完成'],
    up: '工程协同（完结后自动复制）',
    down: '生产工单、委外工单、采购申请单',
    warn: ['PMC 是副本数据，在 PMC 里改数量不会回写核算单和工程协同，源头错了要回源头改。', '原料没提采购申请，仓库就收不到料；工艺没派单，车间就不会开工。']
  },
  {
    id: 'purchase',
    no: '05',
    group: '物料与生产',
    title: '采购申请 → 采购订单',
    role: '采购员',
    path: '/new/purchase/apply/list',
    pathName: '采购申请',
    summary: '采购分两步：先申请单（内部批），再采购订单（发给供应商）。原料需求由 PMC 或工程协同自动带出来。',
    steps: [
      '进入【采购申请】，单据已由 PMC/工程协同生成，点【编辑】补供应商、单价、交期。',
      '点【提交】，采购负责人【审核】。',
      '审核后点【生成采购订单】（列表顶部可勾选多张批量生成）。',
      '进入【采购订单】，【编辑】确认价格交期 → 【提交】→ 【审核】。',
      '审核通过后把订单发给供应商，等送货。'
    ],
    status: ['草稿', '待审核', '已审核（可下推入库）', '作废'],
    up: 'PMC订单 / 工程协同（原料明细）',
    down: '入库单（从采购订单明细下推）',
    warn: ['采购订单不审核，仓库做入库单时选不到这批货。', '只是录错价格用【反审核】退回改，不要直接作废重开，免得对不上源头。']
  },
  {
    id: 'stockIn',
    no: '06',
    group: '物料与生产',
    title: '入库单（货到入库）',
    role: '仓库管理员',
    path: '/new/stock/stockIn/list',
    pathName: '入库单',
    summary: '供应商送货到仓，做入库单。审核之后库存才真正增加，同时生成出入库流水。',
    steps: [
      '进入【入库单】，点【新增入库单】。',
      '点“选择来源”，勾【采购订单明细】（只能选还有“可入库数量”的），点确认。',
      '选仓库、仓位，核对到货数量，保存。',
      '点【审核】：库存主档增加、库存流水生成。',
      '录错了点【反审核】回滚再改；单据不要了用【作废】。'
    ],
    status: ['草稿', '待审核', '已审核（影响库存）', '作废'],
    up: '采购订单明细',
    down: '库存查询 / 收发存汇总（数量增加）',
    warn: ['可入库数量 = 下单数量 − 已入库数量，超量选不上是系统在拦重复入库。', '入库单只保存不审核，等于没入库。']
  },
  {
    id: 'gongdan',
    no: '07',
    group: '物料与生产',
    title: '生产工单 / 委外工单',
    role: '车间主管 / 委外跟单',
    path: '/new/renwu/list',
    pathName: '生产工单',
    summary: '自己车间做的走【生产工单】，发加工商做的走【委外工单】。完工要做“收货入库”，成品才进库存。',
    steps: [
      '工单由 PMC 派单自动生成，在【生产工单】或【委外工单】列表里能看到。',
      '点【编辑】核对工序、物料、数量、交期；生产工单选班组，委外工单选加工商。',
      '点【审核】→【打印】，下发车间或加工商。',
      '做完后点【完工收货入库】，系统生成入库单，成品进库存。'
    ],
    status: ['草稿', '待审核', '已审核（已下发）', '完工收货入库'],
    up: 'PMC订单（派单生成）',
    down: '入库单（完工收货）、库存查询',
    warn: ['不点【完工收货入库】，成品数量不会进库存，发货时就会“账上没货”。', '委外工单回厂同样要走收货入库，别只在纸上记数量。']
  },
  {
    id: 'delivery',
    no: '08',
    group: '交付与资金',
    title: '送货单 + 出库单',
    role: '销售 / 仓库',
    path: '/new/delivery/list',
    pathName: '送货单',
    summary: '给客户发货做【送货单】，仓库账面扣数做【出库单】。两个动作都要做，少一个账就不准。',
    steps: [
      '【送货单】→【新增送货单】→ 选销售单和商品明细（可点“全选当前单号”）→ 填本次送货数量、车号。',
      '点【审核】，再【打印】送货单交司机送客户签收。',
      '【出库单】→【新增出库单】→ 点“选择来源”选销售订单明细 → 选仓库仓位 → 保存。',
      '点【审核】：库存扣减并生成流水。'
    ],
    status: ['送货单：草稿 → 已审核（已发货）', '出库单：草稿 → 待审核 → 已审核'],
    up: '核算单商品明细 / 销售订单',
    down: '库存（减少）、应收（送货金额）',
    warn: ['送货单审核后不会自动扣库存，必须再做一张【出库单】并审核（系统当前已知断点）。', '分次送货就分次开单，数量按本次实际送的计算。']
  },
  {
    id: 'money',
    no: '09',
    group: '交付与资金',
    title: '收款 / 应付',
    role: '财务',
    path: '/finance/overview',
    pathName: '财务总览',
    summary:
      '收款、报销都从订单发起，财务在应收 / 应付里核对。新财务中心看应收账龄、应付账龄、毛利；旧应收/应付列表继续用来开收款单、登记付款。',
    steps: [
      '业务员在订单行内点【开具收款单】，填收款金额、收款方式、收款日期。',
      '财务进【财务总览】看应收未收、逾期、回款、毛利；点【应收账龄】按客户看欠款分布和欠了多久。',
      '【应收列表（旧）】核对收款单并审核，审核后“已收金额”才累加；【应付列表（旧）】登记给供应商的付款。',
      '对客户欠款时，用核算单的合同应收减已审核收款核对（未审核收款不算已收）。'
    ],
    status: ['开具收款单', '财务审核', '已收金额累加'],
    up: '销售订单 / 送货单',
    down: '财务总览、应收账龄、应付账龄、毛利分析',
    warn: [
      '收款单开错要【反审核】，否则已收金额一直挂着，欠款会算错。',
      '只送货没开收款单，应收里不会自动出现这笔钱。',
      '应收金额和旧【应收列表】对不上是正常的：旧列表把暂存、驳回的核算单也算进去，新【应收账龄】只算已审核单。'
    ]
  },
  {
    id: 'stock',
    no: '10',
    group: '交付与资金',
    title: '库存查询与报表',
    role: '仓库 / 财务',
    path: '/new/stock/inventory/list',
    pathName: '库存查询',
    summary: '查库存、对账、盘点、调拨都在库存模块；数据来源就是前面出入库单审核后的结果。',
    steps: [
      '【库存查询】按仓库、材质、规格、批号查当前库存，点“流水”看某笔物料的进出记录。',
      '【盘点单】：新增 → 【从库存生成盘点明细】→ 录实盘数 → 审核，差异直接调库存并记盘盈/盘亏流水（不单独出盘盈单）。',
      '【调拨单】：仓库之间转料，审核后两边同时记账。',
      '【收发存汇总】：按期初 + 收入 − 发出 = 期末 对账，和财务核对。'
    ],
    status: ['盘点单 / 调拨单：草稿 → 待审核 → 已审核（审核才动库存）'],
    up: '入库单、出库单、生产/委外完工入库',
    down: '收发存汇总、财务对账',
    warn: ['库存不对先查流水，看是哪一张出入库单造成，不要直接改库存数。', '盘盈盘亏、调拨都必须审核，否则库存不动。']
  },
  {
    id: 'faq',
    no: '11',
    group: '常用速查',
    title: '常见问题速查',
    role: '所有人',
    summary: '员工最常问的几个问题，遇到卡住先看这里。',
    faqs: [
      {
        q: '单据状态看不懂，怎么读？',
        a: '草稿＝还没提交；待审核＝等负责人点审核；已审核/进行中＝已生效，不能直接改；已完结＝流程走完；已作废＝这张单不算数。'
      },
      {
        q: '【反审核】是干什么的？',
        a: '把已审核的单据退回可编辑状态，并且会回滚它产生的影响（比如入库单反审核会减掉已加的库存）。改完要重新审核。'
      },
      {
        q: '单据找不到了？',
        a: '先看页面顶部的状态页签（全部/待审核/进行中…），再看筛选条件；最后确认这张单是不是被作废了，或账号没有该模块权限。'
      },
      {
        q: '为什么 PMC 里没有我刚审核的核算单？',
        a: '核算单只自动生成【工程协同】；PMC 只有工程协同点【完结】后才会出现。'
      },
      {
        q: '为什么送了货库存没减少？',
        a: '送货单不扣库存，要再做【出库单】并审核。这是目前的已知断点，务必人工补上。'
      },
      {
        q: '为什么生产完了成品不在库存里？',
        a: '工单要执行【完工收货入库】，才会生成入库单；只改工单状态不会进库存。'
      },
      {
        q: '应收金额怎么看才对？',
        a: '看【财务总览 / 应收账龄】：只算已审核核算单，未收 = 合同应收 − 已审核收款。旧【应收列表】把暂存单也统计进去，数字会偏大，核对欠款以账龄为准。'
      },
      {
        q: '客户说付了钱，系统里还显示欠款？',
        a: '先查收款单是不是还停在“待审核”（待审核不计入已收），再查收款单开在哪张核算单上；两者都对就一直挂着，需要财务审核收款单。'
      }
    ]
  }
]

// 流程全景三条线
const LANES = [
  {
    name: '商务线（卖）',
    color: '#396bf6',
    nodes: [
      { title: '客户建档', stepId: 'verify' },
      { title: '核算单', stepId: 'verify' },
      { title: '销售订单', stepId: 'order' },
      { title: '送货单', stepId: 'delivery' },
      { title: '收款单 / 应收', stepId: 'money' }
    ]
  },
  {
    name: '制造线（做）',
    color: '#e6a23c',
    nodes: [
      { title: '工程协同', stepId: 'gongcheng' },
      { title: 'PMC订单', stepId: 'pmc' },
      { title: '生产工单', stepId: 'gongdan' },
      { title: '委外工单', stepId: 'gongdan' },
      { title: '完工收货入库', stepId: 'stockIn' }
    ]
  },
  {
    name: '物料线（料）',
    color: '#46bd87',
    nodes: [
      { title: '采购申请', stepId: 'purchase' },
      { title: '采购订单', stepId: 'purchase' },
      { title: '入库单', stepId: 'stockIn' },
      { title: '库存', stepId: 'stock' },
      { title: '出库单', stepId: 'delivery' }
    ]
  }
]

export default {
  data() {
    return {
      activeId: 'overview',
      steps: STEPS,
      lanes: LANES
    }
  },
  computed: {
    groups() {
      const result = []
      this.steps.forEach((item) => {
        let group = result.find((g) => g.name === item.group)
        if (!group) {
          group = { name: item.group, items: [] }
          result.push(group)
        }
        group.items.push(item)
      })
      return result
    },
    current() {
      return this.steps.find((item) => item.id === this.activeId) || null
    }
  },
  watch: {
    activeId(val) {
      try {
        localStorage.setItem('flow_active_id', val)
      } catch (e) {
        /* ignore */
      }
    }
  },
  mounted() {
    try {
      const last = localStorage.getItem('flow_active_id')
      if (last === 'overview' || this.steps.some((item) => item.id === last)) this.activeId = last
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    // 说明书性质：新开页签打开，不打断当前作业
    openPage(path) {
      window.open(path, '_blank')
    }
  }
}
</script>

<style lang="less" scoped>
.page-main {
  padding-bottom: 10px;
  .page-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .title {
      font-size: 15px;
      font-weight: bold;
    }
  }
}

.flow-wrap {
  display: flex;
  height: calc(100vh - 140px);
  min-height: 460px;
}

/* 左侧导航 */
.flow-nav {
  width: 216px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow-y: auto;
  padding: 6px 0 10px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 3px;
  }
  .nav-group-name {
    padding: 12px 14px 4px;
    font-size: 12px;
    color: #909399;
  }
  .nav-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 13px;
    color: #303133;
    border-left: 3px solid transparent;
    cursor: pointer;
    &:hover {
      background: #f5f7fa;
    }
    &.active {
      background: rgba(57, 107, 246, 0.08);
      border-left-color: #396bf6;
      color: #396bf6;
      font-weight: bold;
    }
    .nav-no {
      width: 24px;
      flex-shrink: 0;
      font-size: 12px;
      color: #909399;
    }
    .nav-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}

/* 右侧内容 */
.flow-body {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c4cc;
    border-radius: 4px;
  }
}

.card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px 14px;
  .card-title {
    font-size: 13px;
    font-weight: bold;
    padding-left: 8px;
    margin-bottom: 10px;
    border-left: 3px solid #396bf6;
    line-height: 1;
  }
}

/* 全景图 */
.lane {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
  &:last-of-type {
    border-bottom: none;
  }
  .lane-name {
    width: 104px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: bold;
    line-height: 30px;
  }
  .lane-nodes {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .node {
    display: inline-block;
    width: 108px;
    padding: 6px 4px;
    margin: 3px 0;
    text-align: center;
    font-size: 12px;
    color: #303133;
    background: #fafafa;
    border: 1px solid #dcdfe6;
    border-left: 3px solid #909399;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
      background: #fff;
      border-color: #396bf6;
      color: #396bf6;
      box-shadow: 0 1px 6px rgba(57, 107, 246, 0.25);
    }
  }
  .arrow {
    color: #c0c4cc;
    margin: 0 4px;
    font-size: 12px;
  }
}
.lane-tip {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
  font-size: 12px;
  color: #909399;
}

.key-line {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #606266;
  line-height: 2;
  b {
    color: #396bf6;
  }
  .key-no {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin: 4px 8px 0 0;
    border-radius: 50%;
    background: #396bf6;
    color: #fff;
    font-size: 12px;
    text-align: center;
    line-height: 18px;
  }
}

.warn-card {
  background: #fffbf5;
  border-color: #f5dab1;
  .warn-title {
    border-left-color: #e6a23c;
    color: #b8741a;
  }
}
.warn-line {
  font-size: 12px;
  color: #b8741a;
  line-height: 1.9;
}

/* 单步说明 */
.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px 14px;
  .step-no {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    margin-right: 10px;
    border-radius: 50%;
    background: #396bf6;
    color: #fff;
    font-size: 15px;
    font-weight: bold;
    text-align: center;
    line-height: 34px;
  }
  .step-title {
    font-size: 15px;
    font-weight: bold;
  }
  .role-tag {
    margin-left: 8px;
    padding: 1px 8px;
    font-size: 12px;
    color: #46bd87;
    background: rgba(70, 189, 135, 0.12);
    border: 1px solid rgba(70, 189, 135, 0.35);
    border-radius: 10px;
  }
  .step-summary {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    line-height: 1.7;
  }
}

.detail-grid {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  .card {
    &:first-child {
      flex: 1.15;
      margin-right: 10px;
    }
    &:last-child {
      flex: 1;
    }
  }
}

// 窄屏上下排，避免两列挤成一团
@media screen and (max-width: 1280px) {
  .detail-grid {
    flex-direction: column;
    .card {
      width: 100%;
      &:first-child {
        flex: none;
        margin-right: 0;
        margin-bottom: 10px;
      }
      &:last-child {
        flex: none;
      }
    }
  }
}

.ops {
  margin: 0;
  padding-left: 18px;
  li {
    font-size: 13px;
    color: #606266;
    line-height: 2;
    margin-bottom: 2px;
    &::marker {
      color: #396bf6;
      font-weight: bold;
    }
  }
}

.status-flow {
  .status-item {
    font-size: 12px;
    color: #303133;
    background: #f4f6fa;
    border-radius: 3px;
    padding: 3px 8px;
    display: inline-block;
    margin-bottom: 4px;
  }
  .status-arrow {
    color: #909399;
    margin: 0 4px;
  }
}

.io {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 1.9;
  & + .io {
    margin-top: 4px;
  }
  .io-label {
    width: 40px;
    flex-shrink: 0;
    color: #909399;
  }
  .io-value {
    flex: 1;
    min-width: 0;
    color: #606266;
  }
}

.qa {
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .qa-q {
    font-size: 13px;
    font-weight: bold;
    color: #303133;
    line-height: 1.8;
  }
  .qa-a {
    font-size: 12px;
    color: #606266;
    line-height: 1.9;
    margin-top: 2px;
  }
}
</style>
